/**
 * CSI MasterFormat Division Mapping Utility
 *
 * Maps modern MasterFormat (2004+, ~50 divisions) to legacy 1995 format (16 divisions).
 * Used for display purposes when customers prefer the older division structure.
 */

// Modern (2004+) to Legacy (1995) Division Mapping
export const MODERN_TO_LEGACY_DIVISION_MAP = {
  // Direct mappings (Divisions 1-14 mostly stay the same)
  0: 0,   // Fee/Procurement (special handling)
  1: 1,   // General Requirements
  2: 2,   // Existing Conditions -> Site Construction
  3: 3,   // Concrete
  4: 4,   // Masonry
  5: 5,   // Metals
  6: 6,   // Wood, Plastics, Composites -> Wood and Plastics
  7: 7,   // Thermal and Moisture Protection
  8: 8,   // Openings -> Doors and Windows
  9: 9,   // Finishes
  10: 10, // Specialties
  11: 11, // Equipment
  12: 12, // Furnishings
  13: 13, // Special Construction
  14: 14, // Conveying Equipment -> Conveying Systems

  // MEP consolidation into Division 15 (Mechanical)
  21: 15, // Fire Suppression -> Mechanical
  22: 15, // Plumbing -> Mechanical
  23: 15, // HVAC -> Mechanical
  25: 15, // Integrated Automation -> Mechanical

  // Electrical consolidation into Division 16
  26: 16, // Electrical
  27: 16, // Communications -> Electrical
  28: 16, // Electronic Safety/Security -> Electrical

  // Sitework consolidation into Division 2 (Site Construction)
  31: 2,  // Earthwork -> Site Construction
  32: 2,  // Exterior Improvements -> Site Construction
  33: 2,  // Utilities -> Site Construction
  34: 2,  // Transportation -> Site Construction
  35: 2,  // Waterway and Marine -> Site Construction

  // Process Equipment (modern divisions 40-49) -> map to closest legacy
  40: 11, // Process Integration -> Equipment
  41: 11, // Material Processing -> Equipment
  42: 11, // Process Heating -> Equipment
  43: 11, // Process Gas Handling -> Equipment
  44: 11, // Pollution Control -> Equipment
  45: 11, // Industry-Specific Manufacturing -> Equipment
  46: 11, // Water/Wastewater Treatment -> Equipment
  48: 16, // Electrical Power Generation -> Electrical
};

// Legacy 1995 16-Division definitions
export const LEGACY_DIVISIONS = {
  0: { number: 0, title: 'Fee / Markup', code: '00' },
  1: { number: 1, title: 'General Requirements', code: '01' },
  2: { number: 2, title: 'Site Construction', code: '02' },
  3: { number: 3, title: 'Concrete', code: '03' },
  4: { number: 4, title: 'Masonry', code: '04' },
  5: { number: 5, title: 'Metals', code: '05' },
  6: { number: 6, title: 'Wood and Plastics', code: '06' },
  7: { number: 7, title: 'Thermal and Moisture Protection', code: '07' },
  8: { number: 8, title: 'Doors and Windows', code: '08' },
  9: { number: 9, title: 'Finishes', code: '09' },
  10: { number: 10, title: 'Specialties', code: '10' },
  11: { number: 11, title: 'Equipment', code: '11' },
  12: { number: 12, title: 'Furnishings', code: '12' },
  13: { number: 13, title: 'Special Construction', code: '13' },
  14: { number: 14, title: 'Conveying Systems', code: '14' },
  15: { number: 15, title: 'Mechanical', code: '15' },
  16: { number: 16, title: 'Electrical', code: '16' },
};

/**
 * Convert a modern division number to its legacy equivalent
 * @param {number} modernDivision - Modern MasterFormat division number (0-49)
 * @returns {number} Legacy division number (0-16)
 */
export function getModernToLegacyDivision(modernDivision) {
  const mapped = MODERN_TO_LEGACY_DIVISION_MAP[modernDivision];
  // If no mapping exists, keep the original (for divisions 1-14 that match)
  return mapped !== undefined ? mapped : modernDivision;
}

/**
 * Extract division number from a category code like "03 00 00" or "03"
 * @param {string} categoryCode - MasterFormat category code
 * @returns {number} Division number
 */
function extractDivisionNumber(categoryCode) {
  if (!categoryCode) return 0;
  // Handle codes like "03 00 00" or "03" - extract first two digits
  const match = categoryCode.match(/^(\d{1,2})/);
  return match ? parseInt(match[1], 10) : 0;
}

/**
 * Group categories by their legacy (1995) division
 * Aggregates modern categories that map to the same legacy division
 *
 * @param {Array} categories - Array of modern category objects
 * @returns {Array} Array of legacy division groups with aggregated data
 */
export function groupCategoriesByLegacyDivision(categories) {
  if (!categories || !Array.isArray(categories)) {
    return [];
  }

  const grouped = {};

  categories.forEach(category => {
    // Get modern division number from category
    const modernDiv = category.divisionNumber ?? extractDivisionNumber(category.categoryCode);
    const legacyDiv = getModernToLegacyDivision(modernDiv);

    if (!grouped[legacyDiv]) {
      const legacyInfo = LEGACY_DIVISIONS[legacyDiv] || {
        number: legacyDiv,
        title: `Division ${legacyDiv}`,
        code: String(legacyDiv).padStart(2, '0'),
      };

      grouped[legacyDiv] = {
        // Use a unique ID for the legacy grouping
        categoryId: `legacy-${legacyDiv}`,
        categoryCode: legacyInfo.code,
        categoryName: legacyInfo.title,
        divisionNumber: legacyDiv,
        legacyDivisionNumber: legacyDiv,
        legacyDivisionTitle: legacyInfo.title,
        // Track the original modern categories for drill-down
        modernCategories: [],
        subtotal: 0,
        lineItems: [],
        isLegacyGroup: true,
      };
    }

    grouped[legacyDiv].modernCategories.push(category);
    grouped[legacyDiv].subtotal += Number(category.subtotal) || 0;

    // Collect all line items, preserving their original category info
    if (category.lineItems && Array.isArray(category.lineItems)) {
      const itemsWithContext = category.lineItems.map(item => ({
        ...item,
        originalCategoryId: category.categoryId,
        originalCategoryName: category.categoryName,
        originalCategoryCode: category.categoryCode,
      }));
      grouped[legacyDiv].lineItems.push(...itemsWithContext);
    }
  });

  // Convert to array and sort by legacy division number
  return Object.values(grouped).sort((a, b) => a.legacyDivisionNumber - b.legacyDivisionNumber);
}

/**
 * Check if a division number represents a "Mechanical" category in legacy format
 * Useful for special handling of combined MEP systems
 * @param {number} legacyDivision - Legacy division number
 * @returns {boolean}
 */
export function isLegacyMechanical(legacyDivision) {
  return legacyDivision === 15;
}

/**
 * Check if a division number represents "Electrical" category in legacy format
 * @param {number} legacyDivision - Legacy division number
 * @returns {boolean}
 */
export function isLegacyElectrical(legacyDivision) {
  return legacyDivision === 16;
}

/**
 * Get a descriptive label for a legacy division that shows what modern divisions it contains
 * @param {number} legacyDivision - Legacy division number
 * @returns {string} Description of what modern divisions are included
 */
export function getLegacyDivisionSubtitle(legacyDivision) {
  switch (legacyDivision) {
    case 2:
      return 'Includes: Site Work, Earthwork, Exterior, Utilities';
    case 15:
      return 'Includes: Fire Suppression, Plumbing, HVAC';
    case 16:
      return 'Includes: Electrical, Communications, Security';
    default:
      return '';
  }
}
